import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useBreakpoints } from '@/hooks/useMediaQuery';
import {
  atualizarStatusPedido,
  conferenciaXmlKeys,
  extractApiError,
  fetchEtiquetaInfo,
  fetchPedidoDetalhe,
  fetchPedidosLista,
  finalizarConferencia,
  isXmlRelatedError,
  mapPedidoDetalheToView,
  reabrirPedido,
} from '@/services/conferenciaXmlService';
import type {
  IConferenciaXmlFiltros,
  IEtiquetaConferida,
  IPedidoSelecionado,
  TAcaoReabrir,
} from '@/types/conferenciaXml';
import { ListaPedidos } from './components/-ListaPedidos';
import { ModalErro, ModalSenha } from './components/-Modals';
import { TelaConferencia } from './components/-TelaConferencia';
import { calcularStatusValidacao, filtrarPedidos } from './-utils';
import { useAnalytics, useErrors, useMetrics } from '@/observability';
import './conferencia-xml.css';

export const Route = createFileRoute('/_authenticated/movimentacao/conferencia-xml/')({
  component: ConferenciaXmlPage,
});

const INITIAL_FILTROS: IConferenciaXmlFiltros = {
  numeroNota: '',
  numeroPedido: '',
  dataInicio: '',
  dataFim: '',
  produto: '',
  status: 'Todos',
};

function ConferenciaXmlPage() {
  const queryClient = useQueryClient();
  const { isMobileOrTablet } = useBreakpoints();
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const metrics = useMetrics();
  const analytics = useAnalytics();
  const errors = useErrors();

  const [filtros, setFiltros] = useState<IConferenciaXmlFiltros>(INITIAL_FILTROS);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [pedidoSelecionado, setPedidoSelecionado] = useState<IPedidoSelecionado | null>(null);
  const [etiquetasConferidas, setEtiquetasConferidas] = useState<IEtiquetaConferida[]>([]);
  const [codigoBarras, setCodigoBarras] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [pedidoAtual, setPedidoAtual] = useState('');
  const [inputPedido, setInputPedido] = useState('');
  const [leituraAtiva, setLeituraAtiva] = useState(false);
  const [pedidoModoResumo, setPedidoModoResumo] = useState(false);
  const [modalSenhaAberto, setModalSenhaAberto] = useState(false);
  const [senhaReabrir, setSenhaReabrir] = useState('');
  const [pedidoParaReabrir, setPedidoParaReabrir] = useState<string | null>(null);
  const [acaoAposReabrir, setAcaoAposReabrir] = useState<TAcaoReabrir | ''>('');
  const [erroSenha, setErroSenha] = useState('');
  const [modalErroAberto, setModalErroAberto] = useState(false);
  const [mensagemModalErro, setMensagemModalErro] = useState('');

  const {
    data: pedidos = [],
    isFetching: loadingPedidos,
    refetch: refetchPedidos,
  } = useQuery({
    queryKey: conferenciaXmlKeys.pedidos,
    queryFn: fetchPedidosLista,
  });

  const pedidosFiltrados = useMemo(() => filtrarPedidos(pedidos, filtros), [pedidos, filtros]);

  const loadPedidoMutation = useMutation({
    mutationFn: ({ numeroPedido, somenteResumo }: { numeroPedido: string; somenteResumo: boolean }) =>
      fetchPedidoDetalhe(numeroPedido, somenteResumo),
    onMutate: () => {
      setErro('');
      setMensagem('');
    },
    onSuccess: (data, { numeroPedido, somenteResumo }) => {
      const pedido = mapPedidoDetalheToView(data);

      if (!somenteResumo && pedido.status !== 'em aberto') {
        setErro(
          `Pedido ${numeroPedido} não está aberto para conferência. Status atual: ${pedido.status}`,
        );
        setPedidoSelecionado(null);
        return;
      }

      setPedidoModoResumo(somenteResumo);
      setPedidoSelecionado(pedido);
      setEtiquetasConferidas([]);
      setMensagem(
        somenteResumo
          ? `Resumo do pedido: ${numeroPedido}`
          : `Dados carregados para o pedido: ${numeroPedido}`,
      );
    },
    onError: (error, { somenteResumo }) => {
      const info = extractApiError(error);
      if (isXmlRelatedError(info)) {
        setErro('');
        setMensagemModalErro(info.detail || info.message);
        setModalErroAberto(true);
      } else {
        setErro(`Erro ao carregar dados do pedido: ${info.message}`);
      }
      setPedidoSelecionado(null);
      setPedidoModoResumo(!!somenteResumo);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ numeroPedido, status }: { numeroPedido: string; status: string }) =>
      atualizarStatusPedido(numeroPedido, { status }),
    onSuccess: (data) => {
      setMensagem(data.message || 'Status atualizado.');
      void refetchPedidos();
    },
    onError: (error) => {
      setErro(`Erro ao atualizar status: ${extractApiError(error).message}`);
    },
  });

  const reabrirMutation = useMutation({
    mutationFn: ({ numeroPedido, senha }: { numeroPedido: string; senha: string }) =>
      reabrirPedido(numeroPedido, { senha }),
    onSuccess: async (_, { numeroPedido }) => {
      setMensagem('Pedido reaberto com sucesso.');
      setModalSenhaAberto(false);
      const acao = acaoAposReabrir;
      setPedidoParaReabrir(null);
      setSenhaReabrir('');
      setAcaoAposReabrir('');
      await refetchPedidos();
      if (acao === 'conferir') {
        loadPedidoMutation.mutate({ numeroPedido, somenteResumo: false });
      }
    },
    onError: (error) => {
      setErroSenha(extractApiError(error).message || 'Senha incorreta.');
    },
  });

  const finalizarMutation = useMutation({
    mutationFn: ({
      numeroPedido,
      codigosBarras,
    }: {
      numeroPedido: string;
      codigosBarras: string[];
    }) =>
      metrics.measure('conferencia_xml.finalizar', () =>
        finalizarConferencia(numeroPedido, {
          codigos_barras: codigosBarras,
          status: 'FINALIZADO',
        }),
      ),
    onSuccess: (_, { codigosBarras }) => {
      analytics.track('Conferencia XML Finalized', { count: codigosBarras.length });
      const total = codigosBarras.length;
      setMensagem(
        `✅ Conferência finalizada com sucesso! ${total} etiquetas conferidas e validadas contra o XML.`,
      );
      setLeituraAtiva(false);
      setEtiquetasConferidas([]);
      setPedidoSelecionado(null);
      setCodigoBarras('');
      setErro('');
      void refetchPedidos();
    },
    onError: (error) => {
      errors.capture(error, { tags: { source: 'conferencia-xml.finalizar' } });
      setErro(`Erro ao finalizar conferência: ${extractApiError(error).message}`);
    },
  });

  const scanMutation = useMutation({
    mutationFn: fetchEtiquetaInfo,
  });

  const loading =
    loadingPedidos ||
    loadPedidoMutation.isPending ||
    statusMutation.isPending ||
    reabrirMutation.isPending ||
    finalizarMutation.isPending ||
    scanMutation.isPending;

  const handleFiltrosChange = (novosFiltros: IConferenciaXmlFiltros) => {
    setFiltros(novosFiltros);
    setPaginaAtual(1);
  };

  const carregarPedido = () => {
    if (!inputPedido.trim()) return;
    const numero = inputPedido.trim();
    setPedidoAtual(numero);
    loadPedidoMutation.mutate({ numeroPedido: numero, somenteResumo: false });
  };

  const abrirModalReabrir = (numeroPedido: string, acao: TAcaoReabrir) => {
    setPedidoParaReabrir(numeroPedido);
    setAcaoAposReabrir(acao);
    setSenhaReabrir('');
    setErroSenha('');
    setModalSenhaAberto(true);
  };

  const confirmarSenhaReabrir = () => {
    if (!pedidoParaReabrir || !senhaReabrir.trim()) {
      setErroSenha('Informe a senha.');
      return;
    }
    setErroSenha('');
    reabrirMutation.mutate({ numeroPedido: pedidoParaReabrir, senha: senhaReabrir.trim() });
  };

  const iniciarLeitura = () => {
    if (!pedidoSelecionado) return;
    setLeituraAtiva(true);
    setEtiquetasConferidas([]);
    setCodigoBarras('');
    setMensagem('Leitura iniciada. Escaneie as etiquetas.');
    setTimeout(() => barcodeInputRef.current?.focus(), 100);
  };

  const finalizarLeitura = () => {
    if (!pedidoSelecionado) return;

    if (etiquetasConferidas.length === 0) {
      setErro('Nenhuma etiqueta foi conferida.');
      return;
    }

    const totalEtiquetasLista = pedidoSelecionado.codigosBarras.length;
    const totalConferidasAtual = etiquetasConferidas.length;

    if (totalConferidasAtual < totalEtiquetasLista) {
      setErro(
        `Conferência incompleta! Conferidas: ${totalConferidasAtual}/${totalEtiquetasLista} etiquetas da lista.`,
      );
      return;
    }

    const codigosConferidos = etiquetasConferidas.map((e) => e.codigoBarras);
    const codigosUnicos = [...new Set(codigosConferidos)];
    if (codigosConferidos.length !== codigosUnicos.length) {
      setErro('Conferência com duplicatas! Alguns códigos foram conferidos mais de uma vez.');
      return;
    }

    const codigosInvalidos = codigosConferidos.filter(
      (codigo) => !pedidoSelecionado.codigosBarras.includes(codigo),
    );
    if (codigosInvalidos.length > 0) {
      setErro(`Conferência com códigos inválidos: ${codigosInvalidos.join(', ')}`);
      return;
    }

    const statusValidacao = calcularStatusValidacao(pedidoSelecionado, etiquetasConferidas);
    if (!statusValidacao.valida) {
      const produtosComErro = statusValidacao.validacoes.filter((v) => v.status === '❌');
      const produtosComErroTexto = produtosComErro
        .map(
          (v) =>
            `• ${v.produto} - ${v.descricao}\n  XML: ${v.qtdeXML.toFixed(2)} KG | Conferido: ${v.qtdeConferida.toFixed(2)} KG | Diferença: ${(v.diferenca ?? v.qtdeXML - v.qtdeConferida).toFixed(2)} KG`,
        )
        .join('\n\n');

      let mensagemProdutosExtras = '';
      if (statusValidacao.produtosExtras.length > 0) {
        mensagemProdutosExtras =
          '\n\nProdutos conferidos que não estão no XML:\n' +
          statusValidacao.produtosExtras
            .map((codigo) => {
              const etiquetas = etiquetasConferidas.filter((e) => e.codigoProduto === codigo);
              return `• ${codigo} - ${etiquetas[0]?.descricao || 'Descrição não disponível'} (${etiquetas.length} etiquetas)`;
            })
            .join('\n');
      }

      setErro(
        `❌ Conferência com discrepâncias de peso encontradas!\n\nProdutos com problemas de peso:\n\n${produtosComErroTexto}${mensagemProdutosExtras}\n\nCorrija as discrepâncias de peso antes de finalizar a conferência.`,
      );
      return;
    }

    finalizarMutation.mutate({
      numeroPedido: pedidoSelecionado.numeroPedido,
      codigosBarras: codigosConferidos,
    });
  };

  const handleBarcodeScan = async () => {
    const codigo = codigoBarras.trim();
    if (!codigo) {
      setErro('Por favor, escaneie um código de barras.');
      return;
    }

    if (!pedidoSelecionado?.codigosBarras) {
      setErro('Pedido não carregado ou sem códigos de barras disponíveis.');
      return;
    }

    if (!pedidoSelecionado.codigosBarras.includes(codigo)) {
      setErro(`Código "${codigo}" não pertence a este pedido.`);
      setCodigoBarras('');
      return;
    }

    if (etiquetasConferidas.some((e) => e.codigoBarras === codigo)) {
      setErro(`Código "${codigo}" já foi conferido.`);
      setCodigoBarras('');
      return;
    }

    try {
      const result = await queryClient.fetchQuery({
        queryKey: conferenciaXmlKeys.etiquetaInfo(codigo),
        queryFn: () => fetchEtiquetaInfo(codigo),
      });

      if (result.codigo_produto) {
        const novaEtiqueta: IEtiquetaConferida = {
          codigoBarras: codigo,
          codigoProduto: result.codigo_produto,
          descricao: result.descricao,
          peso: result.peso_liquido,
          lote: result.lote,
          validade: result.validade,
          pedido: pedidoSelecionado.numeroPedido,
          dataConferencia: new Date().toISOString(),
        };
        const novasEtiquetas = [...etiquetasConferidas, novaEtiqueta];
        setEtiquetasConferidas(novasEtiquetas);
        setCodigoBarras('');
        setErro('');
        setMensagem(
          `✅ Etiqueta "${codigo}" conferida com sucesso! Progresso: ${novasEtiquetas.length}/${pedidoSelecionado.codigosBarras.length}`,
        );
      } else {
        setErro(`Erro ao verificar etiqueta: ${result.error || 'Etiqueta não encontrada'}`);
        setCodigoBarras('');
      }
    } catch (error) {
      setErro(`Erro ao verificar etiqueta: ${extractApiError(error).message}`);
      setCodigoBarras('');
    }
  };

  const handleCodigoBarrasKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      void handleBarcodeScan();
    }
  };

  return (
    <>
      {pedidoSelecionado ? (
        <TelaConferencia
          pedidoSelecionado={pedidoSelecionado}
          pedidoModoResumo={pedidoModoResumo}
          etiquetasConferidas={etiquetasConferidas}
          leituraAtiva={leituraAtiva}
          codigoBarras={codigoBarras}
          isMobileOrTablet={isMobileOrTablet}
          barcodeInputRef={barcodeInputRef}
          onVoltar={() => {
            setPedidoModoResumo(false);
            setPedidoSelecionado(null);
          }}
          onIniciarLeitura={iniciarLeitura}
          onFinalizarLeitura={finalizarLeitura}
          onCodigoBarrasChange={setCodigoBarras}
          onCodigoBarrasKeyDown={handleCodigoBarrasKeyDown}
          onBarcodeScan={() => void handleBarcodeScan()}
        />
      ) : (
        <ListaPedidos
          filtros={filtros}
          onFiltrosChange={handleFiltrosChange}
          pedidosFiltrados={pedidosFiltrados}
          loading={loading}
          erro={erro}
          inputPedido={inputPedido}
          pedidoAtual={pedidoAtual}
          paginaAtual={paginaAtual}
          isMobileOrTablet={isMobileOrTablet}
          onInputPedidoChange={setInputPedido}
          onCarregarPedido={carregarPedido}
          onConferir={(numero) => loadPedidoMutation.mutate({ numeroPedido: numero, somenteResumo: false })}
          onVerResumo={(numero) => loadPedidoMutation.mutate({ numeroPedido: numero, somenteResumo: true })}
          onFinalizar={(numero) => statusMutation.mutate({ numeroPedido: numero, status: 'FINALIZADO' })}
          onReabrir={(numero) => abrirModalReabrir(numero, 'reabrir')}
          onReabrirParaConferir={(numero) => abrirModalReabrir(numero, 'conferir')}
          onReabrirStatus={(numero) => statusMutation.mutate({ numeroPedido: numero, status: 'em aberto' })}
          onPaginaChange={setPaginaAtual}
        />
      )}

      {modalErroAberto && (
        <ModalErro
          mensagem={mensagemModalErro}
          onClose={() => {
            setModalErroAberto(false);
            setMensagemModalErro('');
          }}
        />
      )}

      {modalSenhaAberto && (
        <ModalSenha
          acao={(acaoAposReabrir as TAcaoReabrir) || 'reabrir'}
          senha={senhaReabrir}
          erroSenha={erroSenha}
          onSenhaChange={setSenhaReabrir}
          onConfirmar={confirmarSenhaReabrir}
          onCancelar={() => {
            setModalSenhaAberto(false);
            setPedidoParaReabrir(null);
            setSenhaReabrir('');
            setErroSenha('');
          }}
        />
      )}
    </>
  );
}
